import Card from '@/components/common/Card'
import FormField from '@/components/common/FormField'
import MainHeading from '@/components/common/MainHeading'
import Layout from '@/components/layout/Layout'
import { Button } from '@/components/ui/button'
import type { designation, designationResponse } from '@/interface/designation.interface'
import { DesignationService } from '@/services/Designation/DesignationService'
import { menuBars, showToastNotification } from '@/utility/helper'
import { RoutesLink } from '@/utility/RouteLinks'
import { errorTypes } from '@/utility/validation'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { DepartmentService } from '@/services/Department/Department'
import SearchSelect from '@/components/common/SearchSelect'
import { ThrowError } from '@/api/callApi'
import type { department } from '@/interface/department.interface'
import MultiSelectSearch from '@/components/common/MultiSelectSearch'

const AddDesignation: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams()
    const [isLoading, setIsLoading] = useState(false);
    const [departments, setDepartments] = useState<{ value: string; label: string }[]>([]);
    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<designation>();

    const getDepartments = async () => {
        try {
            const response = await DepartmentService.getDepartment();
            if (response?.status) {
                const formatted = (response?.data?.department || [])?.map((dept: department) => ({
                    value: dept?.id || "",
                    label: dept?.name || ""
                }));
                setDepartments(formatted);
            }
        } catch (error) {
            ThrowError(error);
        }
    }

    const getDesignationById = async () => {
        if (!id) return;
        try {
            const response = await DesignationService.getDesignationById(id);
            if (response?.status) {
                const data = response.data;
                setValue("name", data.name);
                // setValue("isParent", String(data.isParent));
                setValue("level", data.level);
                setValue("canAccessTeam", data.canAccessTeam);
                setValue("isActive", data.isActive);
                setValue("departmentId", data.departmentId);
                setValue("menuBar", data.menuBar || { keyNames: [] });
            }
        } catch (error) {
            ThrowError(error);
        }
    }

    useEffect(() => {
        getDepartments();
        if (id) {
            getDesignationById();
        }
    }, [id]);

    // Manually register custom components that don't use {...register} directly
    useEffect(() => {
        register("departmentId", { ...errorTypes.required("Department") });
        // For MultiSelect, we ensure keyNames array is not empty
        register("menuBar", {
            validate: (value) => ((value?.keyNames?.length ?? 0) > 0 || value?.keyNames === undefined) || "Menu Access is required"
        });
    }, [register]);

    const onSubmit = async (data: designation) => {
        try {
            setIsLoading(true);
            const payload = {
                name: data.name,
                // isParent: data.isParent,
                level: Number(data.level),
                canAccessTeam: String(data.canAccessTeam) === "true",
                isActive: String(data.isActive) === "true",
                departmentId: data.departmentId,
                menuBar: {
                    keyNames: data.menuBar?.keyNames || [],
                },
            }
            let response: designationResponse;
            if (id) {
                response = await DesignationService.updateDesignation(payload, id);
            } else {
                response = await DesignationService.createDesignation(payload);
            }

            if (response?.status) {
                showToastNotification("success", response?.message)
                navigate(RoutesLink.superAdminDesignation)
            } else {
                showToastNotification("danger", response?.message)
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setIsLoading(false);
        }
    }
    console.log("menuBars", menuBars)

    return (
        <Layout>
            <div className='space-y-6'>
                <MainHeading
                    title={id ? "Edit Designation" : "Add Designation"}
                    isBackButtonVisible
                    onClick={() => navigate(-1)}
                />
                <Card className='overflow-visible'>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField
                                {...register("name", { ...errorTypes.required('Name') })}
                                type="text"
                                label="Name"
                                placeholder="Enter Designation Name"
                                error={errors.name?.message}
                            />
                            <SearchSelect
                                label="Department"
                                required
                                placeholder="Select Department"
                                options={departments}
                                value={watch("departmentId")}
                                onChange={(val) => setValue("departmentId", val)}
                                error={errors.departmentId?.message}
                            />
                            {/* <FormField
                                {...register("isParent", { ...errorTypes.required("Is Parent") })}
                                type="text"
                                label="Is Parent"
                            // error={errors.isParent?.message}
                            /> */}
                            <MultiSelectSearch
                                label="Menu Access"
                                required
                                placeholder="Select Menu Access"
                                options={menuBars}
                                value={watch("menuBar")?.keyNames || []}
                                onChange={(val) => setValue("menuBar", { keyNames: val })}
                                error={errors.menuBar?.message}

                            />
                            <FormField
                                {...register("level", { ...errorTypes.required("Level") })}
                                type="number"
                                label="Level"
                                placeholder="Enter Level"
                                error={errors.level?.message}
                            />
                            <FormField
                                {...register("canAccessTeam", { ...errorTypes.required("Can Access Team") })}
                                type="select"
                                label="Can Access Team"
                                placeholder=''
                                value={String(watch("canAccessTeam") ?? "")}
                                options={[
                                    { value: "select", label: "Select Status" },
                                    { value: "true", label: "Active" },
                                    { value: "false", label: "Inactive" },
                                ]}
                                error={errors.canAccessTeam?.message}
                            />

                            <FormField
                                {...register("isActive", { ...errorTypes.required("Status") })}
                                type="select"
                                value={String(watch("isActive") ?? "")}
                                options={[
                                    { value: "select", label: "Select Status" },
                                    { value: "true", label: "Active" },
                                    { value: "false", label: "Inactive" },
                                ]}
                                label="Is Active"
                                error={errors.isActive?.message}
                            />

                        </div>
                        <div className="flex justify-end">
                            <Button disabled={isLoading} type="submit">{isLoading ? "Submitting..." : "Submit"}</Button>
                        </div>
                    </form>
                </Card>
            </div>
        </Layout>
    )
}

export default AddDesignation;
